import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { HierarchyLevelInput } from '../types';

class HierarchyLevelService {
  // Get all hierarchy levels
  async getHierarchyLevels() {
    const query = gql`
      query GetHierarchyLevels {
        hierarchyLevels {
          id
          hierarchyLevelId
          name
          description
          createdDate
          isActive
          isSystemLevel
          isUserLevel
        }
      }
    `;

    try {
      console.log('Attempting to query GraphQL at:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      console.log('GraphQL response:', result);
      return result.data.hierarchyLevels;
    } catch (error: any) {
      console.error('Error fetching hierarchy levels:', error);
      console.error('Network error:', error.networkError);
      console.error('GraphQL errors:', error.graphQLErrors);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch hierarchy levels: ${error.message}`);
      }
    }
  }

  // Get active hierarchy levels only
  async getActiveHierarchyLevels() {
    const query = gql`
      query GetActiveHierarchyLevels {
        activeHierarchyLevels {
          id
          hierarchyLevelId
          name
          description
          createdDate
          isActive
          isSystemLevel
          isUserLevel
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      return result.data.activeHierarchyLevels;
    } catch (error: any) {
      console.error('Error fetching active hierarchy levels:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch active hierarchy levels: ${error.message}`);
      }
    }
  }

  // Get hierarchy level by ID
  async getHierarchyLevelById(id: string) {
    const query = gql`
      query GetHierarchyLevelById($id: String!) {
        hierarchyLevel(id: $id) {
          id
          hierarchyLevelId
          name
          description
          createdDate
          isActive
          isSystemLevel
          isUserLevel
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return result.data.hierarchyLevel;
    } catch (error: any) {
      console.error('Error fetching hierarchy level:', error);
      throw error;
    }
  }

  // Get next available hierarchy level ID
  async getNextHierarchyLevelId() {
    const query = gql`
      query GetNextHierarchyLevelId {
        nextHierarchyLevelId
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      return result.data.nextHierarchyLevelId;
    } catch (error: any) {
      console.error('Error fetching next hierarchy level ID:', error);
      throw error;
    }
  }

  // Create a hierarchy level
  async createHierarchyLevel(input: HierarchyLevelInput) {
    const mutation = gql`
      mutation CreateHierarchyLevel($input: HierarchyLevelInput!) {
        createHierarchyLevel(input: $input) {
          id
          hierarchyLevelId
          name
          description
          createdDate
          isActive
          isSystemLevel
          isUserLevel
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { input }
      });
      return result.data.createHierarchyLevel;
    } catch (error) {
      console.error('Error creating hierarchy level:', error);
      throw error;
    }
  }

  // Update a hierarchy level
  async updateHierarchyLevel(id: string, input: HierarchyLevelInput) {
    const mutation = gql`
      mutation UpdateHierarchyLevel($id: String!, $input: HierarchyLevelUpdateInput!) {
        updateHierarchyLevel(id: $id, input: $input) {
          id
          hierarchyLevelId
          name
          description
          createdDate
          isActive
          isSystemLevel
          isUserLevel
        }
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, input }
      });
      return result.data.updateHierarchyLevel;
    } catch (error) {
      console.error('Error updating hierarchy level:', error);
      throw error;
    }
  }

  // Delete a hierarchy level
  async deleteHierarchyLevel(id: string) {
    const mutation = gql`
      mutation DeleteHierarchyLevel($id: String!) {
        deleteHierarchyLevel(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.deleteHierarchyLevel;
    } catch (error) {
      console.error('Error deleting hierarchy level:', error);
      throw error;
    }
  }

  // Check if hierarchy level can be deleted
  async canDeleteHierarchyLevel(id: string) {
    const mutation = gql`
      mutation CanDeleteHierarchyLevel($id: String!) {
        canDeleteHierarchyLevel(id: $id)
      }
    `;

    try {
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      return result.data.canDeleteHierarchyLevel;
    } catch (error) {
      console.error('Error checking if hierarchy level can be deleted:', error);
      throw error;
    }
  }
}

export const hierarchyLevelService = new HierarchyLevelService();
export default hierarchyLevelService;
